import React, { useEffect, useState } from 'react';
import { XIcon, SendIcon, AlertCircleIcon } from 'lucide-react';
import { useJobMessaging, JobContact } from './JobMessagingContext';
import { useAuth } from '../auth/AuthContext';
interface JobMessageModalProps {
  jobId: string;
  jobName: string;
  recipientRole: 'contractor' | 'broker' | 'foreman' | 'trucking' | 'driver';
  onClose: () => void;
}
export const JobMessageModal: React.FC<JobMessageModalProps> = ({
  jobId,
  jobName,
  recipientRole,
  onClose
}) => {
  const {
    user
  } = useAuth();
  const {
    getJobContacts,
    sendJobMessage
  } = useJobMessaging();
  const [message, setMessage] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [contacts, setContacts] = useState<JobContact[]>([]);
  const [selectedContact, setSelectedContact] = useState<JobContact | null>(null);
  useEffect(() => {
    if (user) {
      // Get contacts for this job based on user role
      const jobContacts = getJobContacts(jobId, user.role);
      // Filter contacts by requested role
      const filteredContacts = jobContacts.filter(contact => contact.role === recipientRole);
      setContacts(filteredContacts);
      // If there's only one contact, select it automatically
      if (filteredContacts.length === 1) {
        setSelectedContact(filteredContacts[0]);
      }
    }
  }, [jobId, user, recipientRole, getJobContacts]);
  const handleSend = () => {
    if (!selectedContact || !message.trim()) return;
    sendJobMessage(jobId, selectedContact.id, message, isUrgent);
    onClose();
  };
  // Safe function to capitalize role name
  const getDisplayRoleName = (role: string | undefined) => {
    if (!role) return 'Contact';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-269">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full" data-id="element-270">
        <div className="flex justify-between items-center p-4 border-b" data-id="element-271">
          <h3 className="font-bold" data-id="element-272">
            Message {getDisplayRoleName(recipientRole)}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-273">
            <XIcon size={20} data-id="element-274" />
          </button>
        </div>
        <div className="p-4" data-id="element-275">
          <div className="mb-4" data-id="element-276">
            <label className="block text-sm font-medium mb-1" data-id="element-277">Job</label>
            <div className="p-2 bg-gray-100 rounded-md" data-id="element-278">{jobName}</div>
          </div>
          {contacts.length > 1 && <div className="mb-4" data-id="element-279">
              <label className="block text-sm font-medium mb-1" data-id="element-280">
                Select {getDisplayRoleName(recipientRole)}
              </label>
              <select className="w-full border rounded-md p-2" value={selectedContact?.id || ''} onChange={e => {
            const selected = contacts.find(c => c.id === e.target.value);
            setSelectedContact(selected || null);
          }} data-id="element-281">
                <option value="" data-id="element-282">
                  -- Select {getDisplayRoleName(recipientRole)} --
                </option>
                {contacts.map(contact => <option key={contact.id} value={contact.id} data-id="element-283">
                    {contact.displayName}
                  </option>)}
              </select>
            </div>}
          {contacts.length === 0 && <div className="mb-4 p-3 bg-yellow-50 text-yellow-800 rounded-md" data-id="element-284">
              No {getDisplayRoleName(recipientRole)} contacts available for this
              job.
            </div>}
          <div className="mb-4" data-id="element-285">
            <label className="block text-sm font-medium mb-1" data-id="element-286">Message</label>
            <textarea className="w-full border rounded-md p-2 h-32 resize-none" placeholder={`Type your message to the ${getDisplayRoleName(recipientRole)}...`} value={message} onChange={e => setMessage(e.target.value)} disabled={contacts.length === 0 || !selectedContact} data-id="element-287"></textarea>
          </div>
          <div className="mb-4" data-id="element-288">
            <label className="flex items-center text-sm cursor-pointer" data-id="element-289">
              <input type="checkbox" checked={isUrgent} onChange={() => setIsUrgent(!isUrgent)} className="mr-2 h-4 w-4 text-red-500" disabled={contacts.length === 0 || !selectedContact} data-id="element-290" />
              <AlertCircleIcon size={14} className="text-red-500 mr-1" data-id="element-291" />
              Mark as urgent
            </label>
          </div>
        </div>
        <div className="flex justify-end p-4 border-t bg-gray-50" data-id="element-292">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 mr-2" data-id="element-293">
            Cancel
          </button>
          <button onClick={handleSend} disabled={!message.trim() || !selectedContact || contacts.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2" data-id="element-294">
            <SendIcon size={16} data-id="element-295" />
            Send
          </button>
        </div>
      </div>
    </div>;
};